import { memo, useContext, useEffect, useMemo, useState } from 'react';
import {
  CardHeader,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Input,
} from 'reactstrap';
import api from 'services/api';
import { PropostaMaterialAvulso as IPropostaMaterialAvulso } from 'models/PropostaMaterialAvulso';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { FaTrash } from 'react-icons/fa';
import { Checkbox, CircularProgress, Tooltip } from '@material-ui/core';
import toast from 'react-hot-toast';
import { PropostaContext } from 'views/pages/Proposta';
import { useQuery } from 'react-query';
import formatCurrency from 'utils/formatCurrency';
import mensagemAtingirMeta from 'utils/mensagemAtingirMeta';
import traduzirProdutoAtividade from 'utils/traduzirProdutoAtividade';
import { arredondaDecimais } from 'utils/arredondaDecimais';
import traduzirNomeMaterial from 'utils/traduzirNomeMaterial';
import { Th, Td, Tr, Descricao } from './styles';
import {
  IMateriaisImportados,
  MateriaisContext,
  PropostaMateriaisSelecao,
} from '../..';

interface props {
  materiaisImportados: IMateriaisImportados[];
  propostaVersaoId: number;
  campoBusca: string;
  isFaturamentoDireto: boolean;
  setPropostaMaterialSelecionado: React.Dispatch<
    React.SetStateAction<PropostaMateriaisSelecao[]>
  >;
  propostaMaterialSelecionado: PropostaMateriaisSelecao[];
  mostrarSelecao: boolean;
}
function PropostaMaterialImportado({
  materiaisImportados,
  propostaVersaoId,
  campoBusca,
  isFaturamentoDireto,
  setPropostaMaterialSelecionado,
  propostaMaterialSelecionado,
  mostrarSelecao,
}: props): JSX.Element {
  const { propostaVersao, setPrecosDesatualizados } =
    useContext(PropostaContext);

  const { triggerUpdateMaterial } = useContext(MateriaisContext);

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [acessoLimitadoListaMateriais, setAcessoLimitadoListaMateriais] =
    useState(true);
  const [idsFiltrados, setIdsFiltrados] = useState<number[]>([]);

  async function getAcessoLimitadoPrograma() {
    const response = await api.get('/acesso-programa/acesso-limitado', {
      params: {
        programaId: 60,
      },
    });

    setAcessoLimitadoListaMateriais(response.data);
  }

  useEffect(() => {
    if (isSaving) {
      toast.loading('Excluindo registro', { position: 'top-right' });
    } else {
      toast.dismiss();
    }
  }, [isSaving]);

  useEffect(() => {
    getAcessoLimitadoPrograma();
  }, []);

  function handleOnAddFinanciamentoDiretoImportado(
    propostaMaterialImportado: any,
  ) {
    setPropostaMaterialSelecionado(prev => {
      const existe = prev.find(
        item => item.id === propostaMaterialImportado.id,
      );

      if (existe) {
        return prev.filter(item => item.id !== propostaMaterialImportado.id);
      }
      return [
        ...prev,
        {
          id: propostaMaterialImportado.id,
          desconto: propostaMaterialImportado?.desconto ?? 0,
          isAvulso: true,
          isFaturamentoDireto,
          codigo:
            propostaMaterialImportado.material.codigoSap ??
            propostaMaterialImportado.material.diteCodigo,
          fabricanteDesc: propostaMaterialImportado.material.descricaoFabrSge,
        },
      ];
    });
  }

  async function deletarImportacao(item: any) {
    const confirmacao = await Swal.fire({
      title: `Atenção`,
      html: `Ao prosseguir, você estará excluindo esse registro de importação da proposta. A operação não poderá ser revertida. Deseja continuar?`,
      icon: 'warning',
      confirmButtonText: `Sim, prosseguir`,
      cancelButtonText: `Não`,
      showCancelButton: true,
    });

    const resultado = confirmacao.isConfirmed;

    if (resultado) {
      setIsSaving(true);
      // Disparar exclusão
      await api.delete(
        `/proposta/versao/${propostaVersao.id}/material-importado/${item.id}`,
      );
      setIsSaving(false);
      if (propostaVersaoId) {
        await mensagemAtingirMeta(
          `Foi removido um equipamento importado da proposta`,
          Number(propostaVersaoId),
        );
      }

      toast.success('Registro excluído com sucesso');
      triggerUpdateMaterial();
    }
  }

  /**
   *
  // pode ser necessário no futuro
  const materiaisFiltrados = useMemo(() => {
    if (!materiaisImportados?.length) return [];

    const agrupado = materiaisImportados[0]?.agrupado;

    const val =
    campoBusca === ''
    ? agrupado
    : agrupado
    .map(val2 =>
    val2.materiais.filter(
      val3 =>
                  val3.material.descricaoFabrSge === campoBusca.toUpperCase(),
    ),
  )
  .filter(val4 => val4.length > 0);

  return val;
  }, [materiaisImportados, campoBusca]);
  */

  function materiais(item: any[]): JSX.Element {
    if (!item.length) {
      return (
        <tr>
          <Td>Nenhum registro encontrado</Td>
        </tr>
      );
    }

    return (
      <>
        {item.map((materialAvulso: any) => {
          const {
            id,
            produtoAtividade,
            valor,
            quantidade,
            valorTotal,
            material,
            desconto,
            custoUnitario,
          } = materialAvulso;

          const desc = desconto ? `${arredondaDecimais(desconto)}%` : '';
          const descontoAtual = propostaMaterialSelecionado.find(
            val => val.id === id,
          )?.desconto;

          const semPreco = valor === 0 || valor === null;
          const semCusto = custoUnitario === 0 || custoUnitario === null;

          let rowTitle;
          if (semPreco && semCusto) {
            rowTitle = 'Material sem valor e sem custo';
          } else if (semPreco) {
            rowTitle = 'Material sem valor';
          } else if (semCusto) {
            rowTitle = 'Material sem custo';
          }

          return (
            <Tr semPreco={semPreco} semCusto={semCusto} title={rowTitle}>
              <Td>
                {produtoAtividade
                  ? traduzirProdutoAtividade(produtoAtividade, true)
                  : ''}
              </Td>
              <Td>
                <Descricao>{traduzirNomeMaterial(material)}</Descricao>
              </Td>
              <Td className="text-center">{quantidade}</Td>
              <Td className="text-center">{desc}</Td>
              <Td className="text-right" hidden={acessoLimitadoListaMateriais}>
                {formatCurrency(valor)}
              </Td>
              <Td className="text-right" hidden={acessoLimitadoListaMateriais}>
                {formatCurrency(valorTotal)}
              </Td>
              {mostrarSelecao && (
                <Td className="text-center w-auto">
                  <Checkbox
                    color="primary"
                    className="mr-1 float-center"
                    checked={propostaMaterialSelecionado.some(
                      (val2: any) => val2.id === materialAvulso.id,
                    )}
                    title="Faturamento Direto"
                    onChange={() =>
                      handleOnAddFinanciamentoDiretoImportado(materialAvulso)
                    }
                  />
                </Td>
              )}
            </Tr>
          );
        })}
      </>
    );
  }

  function atividade(item: any[]): JSX.Element {
    if (!item.length) {
      return (
        <tr>
          <Td className="text-center" colSpan={5}>
            Nenhum registro encontrado
          </Td>
        </tr>
      );
    }

    let itemFiltrado = item;
    if (campoBusca) {
      itemFiltrado = item
        .map(val => ({
          ...val,
          materiais: val.materiais.filter(
            (obj: any) =>
              obj.material.descricaoFabrSge === campoBusca.toUpperCase(),
          ),
        }))
        .filter(val => val.materiais.length > 0);
    }

    const atividadeGrupo = itemFiltrado[0]?.materiais[0].produtoAtividade;

    const atividadee = atividadeGrupo
      ? traduzirProdutoAtividade(atividadeGrupo, true)
      : '';

    return (
      <>
        {itemFiltrado.map((materialAvulso: any) => (
          <>
            {materiais(materialAvulso.materiais)}
            <Tr id={`total_${materialAvulso.atividade}`}>
              <Td colSpan={3} />
              <Td
                className="text-right"
                hidden={acessoLimitadoListaMateriais || campoBusca !== ''}
              >
                <h3>Total Atividade {atividadee}</h3>
              </Td>
              <Td
                className="text-right"
                hidden={acessoLimitadoListaMateriais || campoBusca !== ''}
              >
                <h3>{formatCurrency(materialAvulso.total ?? 0, 2)}</h3>
              </Td>
            </Tr>
          </>
        ))}
      </>
    );
  }

  function getTipoHeader(tipo: any) {
    if (String(tipo.tipoImportacao).toUpperCase() === 'OUTROS') {
      return tipo.finalidade ?? '';
    }
    if (String(tipo.tipoImportacao).toUpperCase() === 'BM') {
      return 'Bombeamento Mestre';
    }

    return tipo.tipo;
  }

  function handleToggleAllTabela(checked: boolean, agrupado: any[]) {
    setPropostaMaterialSelecionado(prev => {
      const idsTabela =
        campoBusca !== ''
          ? agrupado
              .flatMap(g =>
                g.materiais.filter(
                  (m: any) =>
                    m.material.descricaoFabrSge === campoBusca.toUpperCase(),
                ),
              )
              .map((m: any) => m.id)
          : agrupado.flatMap(g => g.materiais.map((m: any) => m.id));

      if (checked) {
        const novos = agrupado
          .flatMap(g => g.materiais)
          .filter(m => idsTabela.includes(m.id))
          .filter(m => !prev.some(p => p.id === m.id))
          .map(m => ({
            id: m.id,
            desconto: m.desconto ?? 0,
            isAvulso: true,
            isFaturamentoDireto,
            codigo: m.material.codigoSap ?? m.material.diteCodigo,
            fabricanteDesc: m.material.descricaoFabrSge,
          }));

        return [...prev, ...novos];
      }
      return prev.filter(p => !idsTabela.includes(p.id));
    });
  }

  function header(item: any): JSX.Element {
    const materiaisIds = item.agrupado.flatMap((g: any) =>
      g.materiais.map((m: any) => m.id),
    );

    const selecionadosIds = new Set(propostaMaterialSelecionado.map(p => p.id));

    const totalMarcados = materiaisIds.filter((id: any) =>
      selecionadosIds.has(id),
    ).length;

    const todosMarcados =
      totalMarcados === materiaisIds.length && materiaisIds.length > 0;
    const algunsMarcados =
      totalMarcados > 0 && totalMarcados < materiaisIds.length;

    return (
      <>
        <CardHeader>
          <Row>
            <Col sm="8">
              <h4 className="mb--1">
                {getTipoHeader(item)} | {item.titulo} |{' '}
                <span style={{ fontSize: '0.7em' }}>{item.data}</span>
              </h4>
              <small className="text-muted"> </small>
            </Col>
            <Col sm="4">
              <Button
                outline
                className="btn-icon btn-2 ml-1 float-right"
                color="danger"
                type="button"
                size="sm"
                disabled={isSaving}
                onClick={() => deletarImportacao(item)}
              >
                <FaTrash />
              </Button>
              <div className="float-right mr-2" style={{ fontSize: '0.7em' }}>
                <strong>Arquivo de importação:</strong>
                <br />
                {item.nomeArquivo}
              </div>
            </Col>
          </Row>
        </CardHeader>

        <Card>
          <CardBody className="p-1">
            {loading ? (
              <Row>
                <Col className="text-center mb-4 mt-4">
                  <CircularProgress />
                </Col>
              </Row>
            ) : (
              <Table bordered responsive>
                <thead>
                  <tr>
                    <Th>Ativ</Th>
                    <Th>Item</Th>
                    <Th className="text-center">Quantidade</Th>
                    <Th className="text-center">Desconto(%)</Th>
                    {!acessoLimitadoListaMateriais && (
                      <>
                        <Th className="text-right">Valor unitário</Th>
                        <Th className="text-right">Valor total</Th>
                      </>
                    )}
                    {mostrarSelecao && (
                      <Th
                        className="text-center w-auto"
                        style={{ width: '80px' }}
                      >
                        <Checkbox
                          color="primary"
                          className="mr-1 float-center"
                          checked={todosMarcados}
                          indeterminate={algunsMarcados}
                          title="Faturamento Direto"
                          onChange={e =>
                            handleToggleAllTabela(
                              e.target.checked,
                              item.agrupado,
                            )
                          }
                        />
                      </Th>
                    )}
                  </tr>
                </thead>
                <tbody>{atividade(item.agrupado)}</tbody>
                <tfoot
                  hidden={acessoLimitadoListaMateriais || campoBusca !== ''}
                >
                  <tr>
                    <td colSpan={3} />
                    <td className="text-right">
                      <h2>Total Materiais:</h2>{' '}
                    </td>
                    <td className="text-right">
                      <h2>{formatCurrency(item.valorTotal, 2)}</h2>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            )}
          </CardBody>
        </Card>
      </>
    );
  }

  return <>{materiaisImportados.map(item => header(item))}</>;
}

export default PropostaMaterialImportado;
